import { compact, includes, isEmpty, map } from 'lodash';

import { INTEGRATION_EXCLUDED_ENTITY_TYPES } from '@/modules/assetProviderIntegrations/shared/constants';
import { BASIC_ASSETS_SUBFORM } from '@/modules/entities/BasicAssetsSubform/BasicAssetsSubform.types';
import {
  ALLOW_MULTIPLE_PRINCIPALS_TYPES,
  BUSINESS_ENTITY_TYPES,
} from '@/modules/entities/entities.constants';
import { getTypeOrUndefined } from '@/modules/entities/EntitySubforms/utils/shared/common.utils';
import {
  estateInclusionStatusToApiTypeMap,
  mapStateInEstateStatusToApi,
} from '@/modules/entities/EntitySubforms/utils/shared/taxStatus.utils';
import { EntityType } from '@/modules/entities/types/EntityType';
import {
  AugmentedCreatePrincipalInput,
  CreateEntityInput,
  EntityAttributionSource,
  EntityStage,
} from '@/types/schema';

import { OWNERSHIP_PERCENTAGE_ENTITY_TYPES } from '../CreateEntityShortForm.constants';
import {
  CREATE_ENTITY_SHORT_FORM_NAMESPACE,
  EntityShortFormShape,
} from '../CreateEntityShortForm.types';

export interface MakeCreateEntityInputParams {
  associatedDocumentIds: string[];
}

export function getCreateEntityInputCommonProps(
  formInput: EntityShortFormShape,
  householdId: string,
  params: MakeCreateEntityInputParams
): CreateEntityInput {
  const entityType = formInput[CREATE_ENTITY_SHORT_FORM_NAMESPACE].entityType;
  const isIntegrationSupported =
    entityType &&
    !INTEGRATION_EXCLUDED_ENTITY_TYPES.includes(entityType as EntityType);

  const integrationEntityIds = isIntegrationSupported
    ? getTypeOrUndefined<string[]>(
        formInput[BASIC_ASSETS_SUBFORM]?.basicAssets?.integrationEntityIds
      )
    : [];

  return {
    householdID: householdId,
    stage: EntityStage.Active,
    attributionSource: EntityAttributionSource.IntakeForm,
    integrationEntityIDs: compact(integrationEntityIds),
    addeparLinkedToNongrantorEntity: (() => {
      // we never want to set this to true if there's no integrationEntityId
      if (isEmpty(integrationEntityIds)) return undefined;
      return getTypeOrUndefined<boolean>(
        formInput[BASIC_ASSETS_SUBFORM]?.basicAssets?.linkToAllAddeparEntities
      );
    })(),
    additionalDocumentIDs: params.associatedDocumentIds,
    defaultDocumentID:
      formInput[CREATE_ENTITY_SHORT_FORM_NAMESPACE]?.defaultDocumentId,
  };
}

export type PrincipalSelectionPattern =
  | 'singleGrantor'
  | 'multipleGrantor'
  | 'multipleGrantorAndEntity';

export function getPrincipalSelectionPattern(
  entityType: EntityType
): PrincipalSelectionPattern {
  if (includes(ALLOW_MULTIPLE_PRINCIPALS_TYPES, entityType)) {
    return 'multipleGrantor';
  }

  if (includes(BUSINESS_ENTITY_TYPES, entityType)) {
    return 'multipleGrantorAndEntity';
  }

  return 'singleGrantor';
}

export function entityTypeAllowsMultipleSelection(entityType: EntityType) {
  return principalPatternAllowsMultipleSelection(
    getPrincipalSelectionPattern(entityType)
  );
}

export function principalPatternAllowsMultipleSelection(
  pattern: PrincipalSelectionPattern
): boolean {
  return (
    pattern === 'multipleGrantor' || pattern === 'multipleGrantorAndEntity'
  );
}

export function getSelectedPrincipalsFromForm(
  principalsInput: EntityShortFormShape['createEntityShortForm']['principals']
) {
  return compact(
    map(principalsInput, (principal, clientProfileId) => {
      if (!principal.isIncluded) return null;
      return {
        clientProfileId,
        ownershipPercentage: principal.ownershipPercentage,
      };
    })
  );
}

export function getSelectedClientProfileIdsFromForm(
  multiplePrincipalsValue:
    | EntityShortFormShape['createEntityShortForm']['principals']
    | undefined,
  singlePrincipalsValue:
    | EntityShortFormShape['createEntityShortForm']['selectedSinglePrincipal']
    | undefined,
  entityType: EntityType
): string[] {
  if (entityTypeAllowsMultipleSelection(entityType)) {
    if (!multiplePrincipalsValue) return [];
    return map(
      getSelectedPrincipalsFromForm(multiplePrincipalsValue),
      (principal) => {
        return principal.clientProfileId;
      }
    );
  }

  return compact([singlePrincipalsValue]);
}

export function makeCreateMultiplePrincipalInputs(
  formInput: EntityShortFormShape,
  householdId: string
): AugmentedCreatePrincipalInput[] {
  const selectedPrincipals = getSelectedPrincipalsFromForm(
    formInput.createEntityShortForm.principals
  );

  // just make sure that ownership percentage isn't being incorrectly assigned; this shouldn't happen but form
  // state can be complicated to reason about, so better safe than sorry
  const shouldPersistOwnershipPercentage = includes(
    OWNERSHIP_PERCENTAGE_ENTITY_TYPES,
    formInput[CREATE_ENTITY_SHORT_FORM_NAMESPACE].entityType
  );

  return selectedPrincipals.map(({ clientProfileId, ownershipPercentage }) => {
    return {
      create: {
        householdID: householdId,
        individualID: clientProfileId,
        ownershipPercentage: shouldPersistOwnershipPercentage
          ? ownershipPercentage
          : null,
      },
    };
  });
}

/**
 * @description asserts that there is exactly one SinglePrincipalInput in the array and returns it
 */
export function getSinglePrincipalInput(
  formInput: EntityShortFormShape,
  householdId: string
): AugmentedCreatePrincipalInput {
  if (!formInput[CREATE_ENTITY_SHORT_FORM_NAMESPACE].selectedSinglePrincipal) {
    throw new Error(
      `Unexpected number of principal inputs for entity type ${formInput[CREATE_ENTITY_SHORT_FORM_NAMESPACE].entityType}`
    );
  }

  return {
    create: {
      householdID: householdId,
      individualID:
        formInput[CREATE_ENTITY_SHORT_FORM_NAMESPACE].selectedSinglePrincipal,
      ownershipPercentage: null,
    },
  };
}

export function getCreateSubtypeInputCommonProps(
  formInput: EntityShortFormShape,
  householdId: string
) {
  return {
    displayName: formInput[CREATE_ENTITY_SHORT_FORM_NAMESPACE].name,
    inEstateStatus:
      estateInclusionStatusToApiTypeMap[
        formInput.createEntityShortForm.estateInclusionStatus
      ],
    stateInEstateStatus: mapStateInEstateStatusToApi(
      formInput.createEntityShortForm.stateInEstateStatus
    ),
    householdID: householdId,
  };
}
